import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { injectIntl } from 'react-intl'
import { Layout, Container, PageTitle, Content } from 'src/components/Layout'
import { InputNumber, TextArea } from 'src/components/Form'
import Loader from 'src/components/Loader/Loader'
import { UploadList, UploadPreview } from 'src/components/Upload'
import { isEmpty, countDecimals, roundDecimals, getBodyContainer } from 'src/utils/helpers'
import withToast from 'src/components/Toast'
import styles from './Item.module.less'
import intlMessagesApp from 'src/modules/App/translations'
import intlMessages from '../translations'
import Modal from 'src/components/Modal/Modal'
import DeleteConfirmation from 'src/modals/DeleteConfirmation/index'
import { Box, Text, Flex } from '@chakra-ui/react'
import { Form, Field } from 'react-final-form'
import { Input } from 'src/components/Form/index'
import Button from 'src/components/Button/Button'

class Item extends React.Component {
  state = {
    name: '',
    quantity: 1,
    price: null,
    notes: '',
    files: [],
    fileToDelete: null,
    fileToPreview: null,
  }

  componentDidMount() {
    const { history, match, getInventoryGroup, inventoryNewItemFilesClear } = this.props

    if (!match.params.groupId) {
      // If there is no Group ID in URL redirect
      history.push('/inventory')
    }

    if (!this.itemId()) {
      inventoryNewItemFilesClear()
    }

    // Load Group data
    getInventoryGroup(match.params.groupId)

    // Load Group Item data
    this.loadItem()
  }

  componentDidUpdate(prevProps) {
    const {
      intl,
      history,
      match,
      getGroupResponse,
      getItemResponse,
      saveItemResponse,
      deleteFileResponse,
      inventoryNewItemFilesClear,
      toast,
    } = this.props

    // If group for which we need to add item is not found
    if (!isEmpty(getGroupResponse) && getGroupResponse !== prevProps.getGroupResponse) {
      // If there was error getting record
      if (getGroupResponse.success !== true) {
        // Redirect back to list of groups
        history.push('/inventory')
      }
    }

    // If it's edit and we received which Item to edit
    if (
      this.itemId() &&
      !isEmpty(getItemResponse) &&
      getItemResponse !== prevProps.getItemResponse
    ) {
      if (
        getItemResponse.success &&
        getItemResponse.hasOwnProperty('item') &&
        !isEmpty(getItemResponse.item)
      ) {
        const item = getItemResponse.item
        this.setState({
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          notes: item.notes || '',
          // enable when edit will be done
          files: item.files || [],
        })
      } else {
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description:
            getItemResponse.message || intl.formatMessage(intlMessagesApp.appErrorDescription),
          status: 'error',
        })
      }
    }

    // If there is save response and it changed (it's empty object while submitting)
    if (!isEmpty(saveItemResponse) && saveItemResponse !== prevProps.saveItemResponse) {
      if (saveItemResponse.success) {
        toast({
          title: intl.formatMessage(intlMessagesApp.appSuccessTitle),
          description: intl.formatMessage(intlMessages.inventoryItemSaveSuccess),
          status: 'success',
        })
        // If we are adding new item, clear "temporary" uploaded files (which should now be saved to new item)
        if (!this.itemId()) {
          inventoryNewItemFilesClear()
        }

        // Redirect back to selected group
        history.push('/inventory/view/' + match.params.groupId || '')
      } else {
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description:
            saveItemResponse.message || intl.formatMessage(intlMessagesApp.appErrorDescription),
          status: 'error',
        })
      }
    }

    // If there was an error while deleting a file
    if (!isEmpty(deleteFileResponse) && deleteFileResponse !== prevProps.deleteFileResponse) {
      if (deleteFileResponse.success) {
        // Load new item data (containing files) if file deleted successfully
        this.loadItem()
      } else {
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description:
            deleteFileResponse.message || intl.formatMessage(intlMessagesApp.appErrorDescription),
          status: 'error',
        })
      }
    }
  }

  loadItem = () => {
    // If it's Item edit
    if (this.itemId()) {
      // Load item data
      this.props.getInventoryItem(this.itemId())
    }
  }

  fileAdded = response => {
    if (
      response &&
      response.hasOwnProperty('file') &&
      response.file.hasOwnProperty('url') &&
      response.file.url
    ) {
      // If editing
      if (this.itemId()) {
        this.setState({
          files: [...this.state.files, response.file],
        })
      } else {
        this.props.inventoryNewItemFilesAdd(response.file)
      }
    }
  }

  fileDeletePrompt = file => {
    this.setState({
      fileToDelete: file,
    })
  }

  fileDelete = file => {
    if (!file) {
      return false
    }

    // When deleting actual file
    if (file.hasOwnProperty('id') && file.id) {
      // Delete file
      this.props.inventoryFileDelete(file.id)
    }

    // When deleting file from S3 and state
    if (file.hasOwnProperty('url') && file.url && file.hasOwnProperty('stateId') && file.stateId) {
      // Delete temporary file from S3
      this.props.inventoryFileDelete(null, file.url)

      // Delete temporary file from state
      this.props.inventoryNewItemFilesRemove(file)
    }

    this.fileDeleteClose()
    this.filePreviewClose()
  }

  fileDeleteClose = () => {
    this.setState({
      fileToDelete: null,
    })
  }

  filePreview = (e, file) => {
    e.preventDefault()

    this.setState({
      fileToPreview: file,
    })
  }

  filePreviewClose = () => {
    this.setState({
      fileToPreview: null,
    })
  }

  itemId = () => {
    const { match } = this.props

    if (match.params.hasOwnProperty('itemId') && match.params.itemId) {
      return match.params.itemId
    }

    return null
  }

  handleSubmit = values => {
    const { group, inventoryItemSave, newItemFiles } = this.props

    inventoryItemSave({
      id: this.itemId(),
      groupId: group && group.id ? group.id : null,
      name: values.name,
      quantity: values.quantity,
      price: values.price,
      notes: values.notes,
      files: newItemFiles,
    })
  }

  validateFormFields = values => {
    const { intl } = this.props
    this.setState({
      price: values.price,
      quantity: values.quantity,
      name: values.name,
      notes: values.notes,
    })

    const errors = {}
    if (!values.name) {
      errors.name = intl.formatMessage(intlMessages.inventoryValidationItemName)
    }

    if (values.name && values.name.length > 100) {
      errors.name = intl.formatMessage(intlMessages.inventoryValidationItemNameLength)
    }

    if (!values.quantity || (values.quantity && values.quantity < 0)) {
      errors.quantity = intl.formatMessage(intlMessages.inventoryValidationItemQuantity)
    }

    if (values.quantity && countDecimals(values.quantity)) {
      errors.quantity = intl.formatMessage(intlMessages.inventoryValidationItemQuantityInvalid)
    }

    if (values.quantity && values.quantity < 0) {
      errors.quantity = intl.formatMessage(intlMessages.inventoryValidationItemQuantityInvalid)
    }

    if (!values.price) {
      errors.price = intl.formatMessage(intlMessages.inventoryValidationItemPrice)
    }
    return errors
  }

  renderPreviewContent = fileToPreview => {
    const { isWidgetState, newItemFiles } = this.props

    const { files } = this.state

    const itemId = this.itemId()
    const savedOrTemporaryFiles = !itemId ? newItemFiles : files
    return (
      <UploadPreview
        isWidget={isWidgetState}
        file={fileToPreview}
        files={savedOrTemporaryFiles}
        onDelete={this.fileDeletePrompt}
        onCancel={this.filePreviewClose}
      />
    )
  }

  getTotalValue = () => {
    const { quantity, price } = this.state
    return quantity && quantity > 0 && price && price > 0
      ? roundDecimals(parseInt(quantity) * price)
      : 0
  }

  allowOnlyNumbers(event) {
    if (event.key && /\D/.test(event.key)) {
      event.preventDefault()
    }
  }

  preventTypingNotNumber(event) {
    if ((event.target.value.includes('.') && event.key === '.') || event.key === ',') {
      event.preventDefault()
    }
  }

  render() {
    const {
      intl,
      isGetGroupInProgress,
      isSaveItemInProgress,
      isGetItemInProgress,
      newItemFiles,
      isWidgetState,
      countryIsoCode,
    } = this.props

    const { files, fileToDelete, fileToPreview } = this.state

    const itemId = this.itemId()
    const addOrEdit = itemId
      ? intl.formatMessage(intlMessagesApp.appGlobalEdit)
      : intl.formatMessage(intlMessagesApp.appGlobalAdd)
    const desktopTitle = addOrEdit + ' ' + intl.formatMessage(intlMessages.inventoryItem)

    const total = this.getTotalValue()

    const submitButtonText = intl.formatMessage(intlMessagesApp.appGlobalSave)

    // When adding new item, we save uploaded files to preserved redux state as "temporary files",
    // otherwise (when editing) we get it from db and into component state
    const savedOrTemporaryFiles = !itemId ? newItemFiles : files

    const incrementQuantity = (args, state) => {
      const { quantity } = this.state

      if (countDecimals(quantity)) {
        return quantity
      }

      const field = state.fields['quantity']
      field.change(parseInt(quantity) + 1)
      this.setState(prevState => ({ quantity: parseInt(prevState.quantity) + 1 }))
    }

    const decrementQuantity = (args, state) => {
      const { quantity } = this.state

      if (countDecimals(quantity)) {
        return quantity
      }

      const field = state.fields['quantity']
      const changedValue = parseInt(quantity) - 1
      if (changedValue < 0) {
        return
      }
      field.change(changedValue)
      this.setState(prevState => ({ quantity: parseInt(prevState.quantity) - 1 }))
    }

    const priceChange = (args, state) => {
      const field = state.fields['price']
      const price = state.formState.values['price']
      const formattedPrice = price.replace(/[^.\d]/g, '').replace(/^(\d*\.?)|(\d*)\.?/g, '$1$2')
      const splicedPrice =
        countDecimals(formattedPrice) > 2
          ? (Math.floor(formattedPrice * 100) / 100).toFixed(2)
          : formattedPrice

      field.change(splicedPrice)
      this.setState({ price: splicedPrice })
    }

    const getCurrencySymbolIcon = () => {
      const isUk = countryIsoCode && countryIsoCode.toLowerCase() === 'gb'
      return isUk ? 'pound-sign' : 'dollar-sign'
    }

    return (
      <Layout>
        <Content>
          <PageTitle
            title={desktopTitle}
            showBack={true}
            subtitle={
              total > 0 ? (
                <div
                  className={
                    styles.total +
                    ' kiosk--chakra-colors-header kiosk--chakra-colors-background-primary'
                  }
                >
                  <span className={styles.totalTitle}>
                    {intl.formatMessage(intlMessages.inventoryTotalValue)} |{' '}
                  </span>
                  <span className={styles.totalValue}>${total}</span>
                </div>
              ) : (
                ''
              )
            }
          />

          <Container className={styles.container}>
            {isGetGroupInProgress || isSaveItemInProgress || isGetItemInProgress ? (
              <Loader styleVariation='staticWithHeight' />
            ) : (
              <>
                <div className={styles.files}>
                  <UploadList
                    isWidget={isWidgetState}
                    itemPreviewOnly={false}
                    files={savedOrTemporaryFiles}
                    addText={intl.formatMessage(intlMessagesApp.picturesAddPhoto)}
                    onFileAdded={this.fileAdded}
                    onFileDelete={this.fileDeletePrompt}
                    onFilePreview={this.filePreview}
                    size={true}
                    dropZoneOptions={{
                      onDropHandlerUrl: '/inventory/inventoryFile',
                      onDropData: {
                        itemId: itemId,
                      },
                      supportedUploadTypes: [
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                        'image/gif',
                        'image/svg',
                      ],
                    }}
                  />
                </div>
                <Form
                  onSubmit={this.handleSubmit}
                  validate={this.validateFormFields}
                  mutators={{ incrementQuantity, decrementQuantity, priceChange }}
                  initialValues={{
                    name: this.state.name,
                    quantity: this.state.quantity,
                    price: this.state.price,
                    notes: this.state.notes,
                  }}
                  render={({ handleSubmit, form }) => (
                    <form onSubmit={handleSubmit}>
                      <div className={styles.formContent}>
                        <div>
                          <div className={styles.formField}>
                            <Field
                              name='name'
                              render={({ input, meta }) => (
                                <Box mb={2.5}>
                                  <Input
                                    name='name'
                                    placeholder={intl.formatMessage(
                                      intlMessages.inventoryPlaceholderItemName,
                                    )}
                                    error={meta}
                                    type='text'
                                    {...input}
                                  />
                                </Box>
                              )}
                            />
                          </div>
                          <div className={styles.quantityAndPrice}>
                            <div className={styles.formFieldQuantity}>
                              <Box mb={'10px'}>
                                <Text fontWeight={'bold'} fontSize={'14px'} variant='secondary'>
                                  {intl.formatMessage(intlMessages.inventoryLabelQuantity)}
                                </Text>
                              </Box>
                              <Field
                                name='quantity'
                                render={({ input, meta }) => (
                                  <Box mb={2.5}>
                                    <InputNumber
                                      name='quantity'
                                      maxLength={21}
                                      onInputKeyPress={this.allowOnlyNumbers}
                                      onRightIconClick={form.mutators.incrementQuantity}
                                      onLeftIconClick={form.mutators.decrementQuantity}
                                      iconOnLeft={['far', 'minus']}
                                      iconOnRight={['far', 'plus']}
                                      error={meta}
                                      {...input}
                                    />
                                  </Box>
                                )}
                              />
                            </div>
                            <div className={styles.formFieldPrice}>
                              <Box mb={'10px'}>
                                <Text fontWeight={'bold'} fontSize={'14px'} variant='secondary'>
                                  {intl.formatMessage(intlMessages.inventoryLabelPrice)}
                                </Text>
                              </Box>

                              <Field
                                name='price'
                                render={({ input, meta }) => (
                                  <Box mb={2.5}>
                                    <InputNumber
                                      name='price'
                                      error={meta}
                                      onInputKeyPress={this.preventTypingNotNumber}
                                      onInputChange={form.mutators.priceChange}
                                      placeholder={'0.00'}
                                      iconOnLeft={['far', getCurrencySymbolIcon()]}
                                      {...input}
                                    />
                                  </Box>
                                )}
                              />
                            </div>
                          </div>
                          <div className={styles.formField}>
                            <Box mb={'10px'}>
                              <Text fontWeight={'bold'} fontSize={'14px'} variant='secondary'>
                                {intl.formatMessage(intlMessages.inventoryLabelNotes)}
                              </Text>
                            </Box>

                            <Field
                              name='notes'
                              render={({ input }) => (
                                <Box mb={5}>
                                  <TextArea
                                    name='notes'
                                    type='text'
                                    placeholder={intl.formatMessage(
                                      intlMessages.inventoryPlaceholderNotes,
                                    )}
                                    {...input}
                                  />
                                </Box>
                              )}
                            />
                          </div>
                        </div>
                        <Flex mt={'30px'} justify='center'>
                          <Box maxW={{ lg: '440px', base: '100%' }} w={'100%'}>
                            <Button isFullWidth type='submit'>
                              {submitButtonText}
                            </Button>
                          </Box>
                        </Flex>
                      </div>
                    </form>
                  )}
                />
                <Modal
                  isOpen={fileToPreview}
                  isCentered
                  footer={null}
                  onOk={this.submitModal}
                  onClose={() => this.setState({ fileToPreview: null })}
                  renderContent={() => this.renderPreviewContent(fileToPreview)}
                  getContainer={() => getBodyContainer()}
                  hideFooter={true}
                  closable
                ></Modal>
              </>
            )}
            <DeleteConfirmation
              icon={['far', 'trash-can']}
              heading={`${intl.formatMessage(intlMessagesApp.appGlobalDelete)} photo`}
              confirmText={intl.formatMessage(intlMessagesApp.appGlobalDelete)}
              visible={fileToDelete}
              onClose={this.fileDeleteClose}
              renderContent={() => <></>}
              onConfirm={() => this.fileDelete(fileToDelete)}
            />
          </Container>
        </Content>
      </Layout>
    )
  }
}

Item.propTypes = {
  isGetGroupInProgress: PropTypes.bool.isRequired,
  getGroupResponse: PropTypes.object.isRequired,
  group: PropTypes.object.isRequired,
  getInventoryGroup: PropTypes.func.isRequired,
  getInventoryItem: PropTypes.func.isRequired,
  isGetItemInProgress: PropTypes.bool.isRequired,
  getItemResponse: PropTypes.object.isRequired,
  inventoryItemSave: PropTypes.func.isRequired,
  isSaveItemInProgress: PropTypes.bool.isRequired,
  saveItemResponse: PropTypes.object.isRequired,
  inventoryFileDelete: PropTypes.func.isRequired,
  deleteFileResponse: PropTypes.object.isRequired,
  newItemFiles: PropTypes.array.isRequired,
  inventoryNewItemFilesAdd: PropTypes.func.isRequired,
  inventoryNewItemFilesRemove: PropTypes.func.isRequired,
  inventoryNewItemFilesClear: PropTypes.func.isRequired,
  labels: PropTypes.object.isRequired,
  intl: PropTypes.object,
  router: PropTypes.object,
}

export default withRouter(injectIntl(withToast(Item)))
